import { API, graphqlOperation } from "aws-amplify";
import { ResourceType, UNSPOTTED_TRACK_ID } from "../../utils/constants";
import {
  updateOutboundShipment,
  updateOutboundShipmentMini,
} from "./custom-mutations";

// performDeleteAccount - call graphQl, no try-catch
// handleCreateAccount - with try-catch
// processUpdateAccount - process for lambda

export const sanitizeOutboundLambda = ({
  account,
  area,
  billToAccount,
  driver, // @connection object — keep driverId/driverName scalars (carrier is a String, leave it)
  invoice,
  invoicedServices,
  location,
  manualCharges,
  photos,
  railEquipmentType,
  reloadYardCommodity,
  savedAddress, // UI-only edit-dialog draft (AddressBookSelect) — never persist
  seals,
  shipmentLineItems,
  shipWithGroupId, // lambda-owned (processOutbound /outboundShipWith) — a stale form must not clobber a link (AD-9)
  shipWithIds,
  timeslot,
  trackId, // shipment-root — omit from mutation input; canonical placement is trackCar.trackId
  trackCar,
  truckEquipmentType,
  ...rest
}) => rest;

export const sanitizeOutbound = (outbound) => {
  const sanitized = sanitizeOutboundLambda(outbound);
  const { createdAt, updatedAt, ...rest } = sanitized;
  return rest;
};

/**
 * Build the narrow outbound header payload for a schedule-only save — the
 * mirror of buildInboundScheduleUpdateInput.
 *
 * A carrier booking a pickup edits one field, so the write should touch one
 * field. Going through the full-record writer instead re-sent every scalar,
 * line item and inventory aggregate from a form snapshot taken minutes
 * earlier, which quietly reverted whatever the yard changed in the meantime.
 * This carries the appointment fields plus the inputs processOutbound needs to
 * rebuild the composite index key that embeds scheduledDateTime — status,
 * endDateTime, arrivalDateTime and createDateTime are read for that key, never
 * written back as fields of their own.
 *
 * `expectedCurrentTimeslotId`, `expectedCurrentScheduledDateTime` and
 * `expectedStatus` become the write's conditions: the transaction fails rather
 * than booking against a shipment that has since been re-booked, rescheduled or
 * moved past PENDING. Pass null as timeslotId to clear an existing appointment.
 *
 * History is appended, not set (`historyAppendList`), so a note the yard added
 * while the carrier's form was open survives the booking.
 */
export const buildOutboundScheduleUpdateInput = (
  outbound,
  {
    timeslotId = undefined,
    expectedCurrentTimeslotId = undefined,
    expectedCurrentScheduledDateTime = undefined,
    expectedStatus = undefined,
    historyAppendList = [],
  } = {},
) => {
  if (!outbound?.id) return null;

  if (timeslotId === undefined) {
    throw new Error(
      "buildOutboundScheduleUpdateInput requires timeslotId. Pass null to clear an existing appointment.",
    );
  }

  const input = {
    id: outbound.id,
    // Index-key inputs (read, not written): processOutbound rebuilds
    // "status#endDateTime#arrivalDateTime#scheduledDateTime#createDateTime"
    // from these. "status#active" is deliberately left alone — it does not
    // move with an appointment, and rewriting it from this snapshot could
    // resurrect a shipment archived while the form was open.
    status: outbound.status,
    endDateTime: outbound.endDateTime ?? null,
    arrivalDateTime: outbound.arrivalDateTime ?? null,
    createDateTime: outbound.createDateTime ?? null,
    scheduledDateTime: outbound.scheduledDateTime || null,
    timeslotId,
    historyAppendList,
    // Lambda owns this write; never echo the form's loaded updatedAt (stale vs
    // stream/subscribers).
    updatedAt: new Date().toISOString(),
    expectedCurrentTimeslotId,
  };

  // The appointment time the caller READ, not the one it is writing —
  // input.scheduledDateTime above is the new value. A reschedule reuses the
  // timeslot row, so expectedCurrentTimeslotId cannot tell that someone else
  // moved the booking; this can.
  if (expectedCurrentScheduledDateTime !== undefined)
    input.expectedCurrentScheduledDateTime = expectedCurrentScheduledDateTime;

  // Optional: the manager paths change status as part of their save and must
  // not condition on the value they are replacing.
  if (expectedStatus !== undefined) input.expectedStatus = expectedStatus;

  return input;
};

/** Build TrackCar create input (single shape for lambda and GraphQL create). */
export const buildTrackCarCreateInput = (outbound) => {
  if (!outbound?.id) return null;
  const createdAt = outbound.createdAt || new Date().toISOString();
  return {
    id: outbound.id,
    __typename: "TrackCar",
    owner: outbound.owner,
    active: "true",
    reloadYardId: outbound.reloadYardId,
    accountId: outbound.accountId,
    shipmentId: outbound.id,
    parentType: ResourceType.OUTBOUND,
    railCarNumber: outbound.railCarNumber || "",
    trackId: UNSPOTTED_TRACK_ID,
    eodTrackId: UNSPOTTED_TRACK_ID,
    eodSortKey: "",
    sortKey: "",
    createdAt,
    updatedAt: createdAt,
  };
};

/** Build TrackCar update input for rail-to-rail shipment edits. */
export const buildTrackCarUpdateInput = (outbound) => {
  if (!outbound?.id) return null;
  return {
    id: outbound.id,
    railCarNumber: outbound.railCarNumber || "",
    updatedAt: new Date().toISOString(),
  };
};

export const sanitizeShipmentLineItemLambda = ({
  inventory,
  inventoryItems,
  outboundShipment,
  ...rest
}) => rest;

export const sanitizeShipmentLineItem = (shipmentLineItem) => {
  const sanitized = sanitizeShipmentLineItemLambda(shipmentLineItem);
  const { createdAt, updatedAt, ...rest } = sanitized;
  return rest;
};

// custom-mutations.updateInboundShipment
export const performUpdateOutbound = async (outbound) => {
  const outboundInput = sanitizeOutbound(outbound);
  return API.graphql(
    graphqlOperation(updateOutboundShipment, { input: outboundInput }),
  );
};

// custom-mutations.updateInboundShipmentMini
export const performUpdateOutboundMini = async (outbound) => {
  const outboundInput = sanitizeOutbound(outbound);

  return API.graphql(
    graphqlOperation(updateOutboundShipmentMini, { input: outboundInput }),
  );
};
